import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import clsx from 'clsx'
import { useQueryClient } from '@tanstack/react-query'
import { useModal } from '@ebay/nice-modal-react'
import {
  Input,
  useMessage,
  Avatar,
  BulkInProgressModal,
  Dropdown,
  Tooltip,
} from '@pepper/ui'

import {
  useBulkUpdateItemAssignee,
  useBulkUpdateOrderAssignee,
  useRemoveAssignee,
  useUpdateItemAssignee,
} from 'utils/hooks/workflows'
import Assignee from '../Assignee'

import s from './AssigneeDropdown.module.css'

type Props = {
  companyId: string
  isPrivateDoc: boolean
  showTooltip: boolean
  showSelected?: boolean
  assigneeList?: TeamMembers[]
  itemId?: string
  bulkItems?: Item[] | Assignment[]
  assigneeId?: string
  children?: JSX.Element
}

export const AssigneeDropdown = ({
  companyId,
  isPrivateDoc,
  showTooltip,
  showSelected = true,
  assigneeList,
  itemId,
  bulkItems,
  assigneeId,
  children,
}: Props) => {
  const [assigneeData, setAssigneeData] = useState(
    assigneeList?.filter(assignee => assignee.id)
  )
  const [isOpen, setIsOpen] = useState(false)
  const { showSuccess, showError } = useMessage()
  const queryClient = useQueryClient()
  const bulkProgressModal = useModal(BulkInProgressModal)
  const { mutateAsync: bulkItemAssigneeUpdate } = useBulkUpdateItemAssignee()
  const { mutateAsync: bulkOrderAssigneeUpdate } = useBulkUpdateOrderAssignee()
  const { mutateAsync: removeAssignee } = useRemoveAssignee()
  const { mutateAsync: updateItemAssignee } = useUpdateItemAssignee()

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filteredAssigneeList = assigneeList?.filter(assignee =>
      assignee.full_name.toLowerCase().includes(event.target.value)
    )
    setAssigneeData(filteredAssigneeList)
  }

  const handleClick = async (assigneeID: string) => {
    if (itemId) {
      let response
      if (isPrivateDoc) {
        response = await updateItemAssignee({
          itemId,
          workflowAssigneeId: assigneeID,
        })
      } else {
        response = await bulkOrderAssigneeUpdate({
          assignments: [
            {
              id: itemId,
              workflow_assignee_id: assigneeID,
            },
          ],
          companyId,
        })
      }

      if (response.status === 'success') {
        queryClient.invalidateQueries(['workflow-log'])
        queryClient.invalidateQueries([
          isPrivateDoc ? 'doc-items' : 'order-items',
        ])
        !isPrivateDoc &&
          queryClient.invalidateQueries([`/assignments/:${itemId}`])
        showSuccess('User updated successfully!')
      } else {
        showError('Something went wrong!')
      }
    } else if (bulkItems && bulkItems.length > 0) {
      const bulkPayload = bulkItems.map(item => {
        return {
          id: item.id,
          workflow_assignee_id: assigneeID,
        }
      })
      let response
      if (isPrivateDoc) {
        response = await bulkItemAssigneeUpdate({
          items: bulkPayload,
          companyId,
        })
      } else {
        response = await bulkOrderAssigneeUpdate({
          assignments: bulkPayload,
          companyId,
        })
      }

      if (response.status === 'success') {
        queryClient.invalidateQueries(['workflow-log'])
        queryClient.invalidateQueries([
          isPrivateDoc ? 'doc-items' : 'order-items',
        ])
        bulkProgressModal.show({
          transactionId: response.transaction_id,
          isBulkWorkflow: true,
        })
      } else {
        showError('Could not update all selected items')
      }
    }
    setIsOpen(false)
  }

  const handleRemove = async () => {
    if (itemId) {
      let response
      if (isPrivateDoc) {
        response = await removeAssignee({ itemId })
      } else {
        response = await bulkOrderAssigneeUpdate({
          assignments: [
            {
              id: itemId,
              workflow_assignee_id: null,
            },
          ],
          companyId,
        })
      }

      if (response.status === 'success') {
        queryClient.invalidateQueries(['workflow-log'])
        queryClient.invalidateQueries([
          isPrivateDoc ? 'doc-items' : 'order-items',
        ])
        !isPrivateDoc &&
          queryClient.invalidateQueries([`/assignments/:${itemId}`])
        showSuccess('User removed successfully')
      } else {
        showError('Something went wrong!')
      }
    } else if (bulkItems && bulkItems.length > 0) {
      const bulkPayload = bulkItems.map(item => {
        return {
          id: item.id,
          workflow_assignee_id: null,
        }
      })
      let response
      if (isPrivateDoc) {
        response = await bulkItemAssigneeUpdate({
          items: bulkPayload,
          companyId,
        })
      } else {
        response = await bulkOrderAssigneeUpdate({
          assignments: bulkPayload,
          companyId,
        })
      }

      if (response.status === 'success') {
        queryClient.invalidateQueries(['workflow-log'])
        queryClient.invalidateQueries([
          isPrivateDoc ? 'doc-items' : 'order-items',
        ])
        bulkProgressModal.show({
          transactionId: response.transaction_id,
          isBulkWorkflow: true,
        })
      } else {
        showError('Could not update all selected items')
      }
    }
    setIsOpen(false)
  }

  useEffect(() => {
    setAssigneeData(assigneeList?.filter(assignee => assignee.id))
  }, [assigneeList])

  const assignedUser = assigneeList
    ?.filter(_assignee => _assignee.id)
    ?.filter(assignee => assignee.id === assigneeId)

  return (
    <Dropdown
      overlay={
        <div className={s.container}>
          <Input
            prefix={<Icon icon="lucide:search" className={s.searchIcon} />}
            className={s.inputBox}
            placeholder="Assign to..."
            onChange={handleSearch}
          />
          <div className={s.listArea}>
            <div
              className={clsx(s.item, {
                [s.selected]: !assignedUser?.[0]?.id && showSelected,
              })}
              role="none"
              onClick={handleRemove}
            >
              <div className={s.itemLeft}>
                <div className={s.noAssignee}>
                  <Icon icon="lucide:user" />
                </div>
                <div>No Assignee</div>
              </div>
              {assignedUser?.length === 0 && showSelected && (
                <Icon icon="mdi:tick" className={s.tickIcon} />
              )}
            </div>
            {assigneeData?.map(assignee => {
              const isSelected =
                showSelected && assignedUser?.[0]?.id === assignee.id

              return (
                <div
                  key={assignee.id}
                  className={clsx(s.item, {
                    [s.selected]: isSelected,
                  })}
                  role="none"
                  onClick={() => handleClick(assignee.id)}
                >
                  <div className={s.itemLeft}>
                    {assignee.avatar ? (
                      <Avatar
                        src={assignee.avatar}
                        className={s.assigneeAvatar}
                        size={24}
                      />
                    ) : (
                      <Avatar className={s.assigneeAvatar} size={24}>
                        {assignee.full_name[0]}
                      </Avatar>
                    )}
                    <div>{assignee.full_name}</div>
                  </div>
                  {isSelected && (
                    <Icon icon="mdi:tick" className={s.tickIcon} />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      }
      placement="bottomLeft"
      trigger={['click']}
      visible={isOpen}
      onVisibleChange={setIsOpen}
    >
      {showTooltip && !isOpen ? (
        <Tooltip
          overlay={<Assignee assigneeData={assignedUser?.[0]} />}
          placement="topLeft"
          popupVisible={false}
        >
          {children}
        </Tooltip>
      ) : (
        children
      )}
    </Dropdown>
  )
}
